import React from "react";
import "./styles.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Housings } from "../Housings";
import { HousingComplex } from "../HousingComplex";
import { BrandProvider, useBranding } from "../../integrations/branding";

const BrandedShowcaseLayout: React.FC = () => {
  const { brandId, config } = useBranding();
  const basePath = `/brand=${brandId}`;
  const palette = config.showcase.palette;

  return (
    <div
      className="brand-showcase"
      style={
        {
          "--brand-page-background": palette.pageBackground,
          "--brand-page-background-start": palette.pageBackgroundStart,
          "--brand-page-background-end": palette.pageBackgroundEnd,
          "--brand-surface": palette.surface,
          "--brand-surface-alt": palette.surfaceAlt,
          "--brand-surface-elevated": palette.surfaceElevated,
          "--brand-border": palette.border,
          "--brand-accent": palette.accent,
          "--brand-accent-strong": palette.accentStrong,
          "--brand-accent-contrast": palette.accentContrast,
          "--brand-text-primary": palette.textPrimary,
          "--brand-text-secondary": palette.textSecondary,
          "--brand-grid-line": palette.gridLine,
          "--brand-glow-primary": palette.glowPrimary,
          "--brand-glow-secondary": palette.glowSecondary,
          "--brand-badge-background": palette.badgeBackground,
          "--brand-badge-text": palette.badgeText,
          "--brand-tab-active-background": palette.tabActiveBackground,
          "--brand-tab-active-text": palette.tabActiveText,
          "--brand-input-background": palette.inputBackground,
          "--brand-input-highlight": palette.inputHighlight,
          "--brand-secondary-button-background": palette.secondaryButtonBackground,
          "--brand-secondary-button-text": palette.secondaryButtonText,
          "--brand-secondary-button-border": palette.secondaryButtonBorder,
          "--brand-skeleton-base": palette.skeletonBase,
          "--brand-skeleton-highlight": palette.skeletonHighlight,
          "--brand-logo-width": `${config.showcase.logoWidth}px`,
          "--brand-logo-image-height": `${config.showcase.logoImageHeight}px`,
          "--brand-logo-text-size": `${config.showcase.logoTextSize}px`,
          "--brand-logo-text-color": config.showcase.logoTextColor,
        } as React.CSSProperties
      }
    >
      <div className="brand-showcase_shell">
        <header className="brand-showcase_header">
          <div className="brand-showcase_topbar">
            <div className="brand-showcase_logo-wrap">
              <div
                className={`brand-showcase_logo ${
                  config.showcase.logoLayout === "text-bottom"
                    ? "is-text-bottom"
                    : "is-text-right"
                } ${
                  config.showcase.logoMode === "image"
                    ? "is-image-only"
                    : config.showcase.logoMode === "image-text"
                      ? "is-image-text"
                      : "is-text-only"
                }`}
              >
                {(config.showcase.logoMode === "image" ||
                  config.showcase.logoMode === "image-text") &&
                config.showcase.logoSrc ? (
                  <img
                    src={config.showcase.logoSrc}
                    alt={config.showcase.logoAlt || config.displayName}
                  />
                ) : null}

                {(config.showcase.logoMode === "text" ||
                  config.showcase.logoMode === "image-text") &&
                config.showcase.logoText ? (
                  <span>{config.showcase.logoText}</span>
                ) : null}
              </div>
            </div>

            <div className="brand-showcase_section-label">
              {config.showcase.heroLabel ? (
                <>
                  <span className="brand-showcase_section-line" />
                  <span>{config.showcase.heroLabel}</span>
                </>
              ) : null}
            </div>
          </div>

          {config.showcase.heroTitle || config.showcase.heroDescription ? (
            <div className="brand-showcase_intro">
              {config.showcase.heroTitle ? (
                <h1 className="brand-showcase_title">{config.showcase.heroTitle}</h1>
              ) : null}
              {config.showcase.heroDescription ? (
                <p className="brand-showcase_description">
                  {config.showcase.heroDescription}
                </p>
              ) : null}
            </div>
          ) : null}
        </header>

        <main className="brand-showcase_content">
          <Routes>
            <Route
              path={basePath}
              element={
                <Housings
                  basePath={basePath}
                  enabledHousingTypes={config.showcase.enabledHousingTypes}
                />
              }
            />
            <Route
              path={`${basePath}/housing-complex/:id/`}
              element={<HousingComplex key="brand-residential" type="residential" basePath={basePath} />}
            />
            <Route
              path={`${basePath}/commerce-complex/:id/`}
              element={<HousingComplex key="brand-commerce" type="commerce" basePath={basePath} />}
            />
            <Route path="*" element={<Navigate to={basePath} replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export const BrandedShowcase: React.FC = () => {
  const location = useLocation();
  const brand = location.pathname
    .replace(/^\/brand=/, "")
    .split("/")[0]
    .trim();

  return (
    <BrandProvider brandSlug={brand} isShowcase>
      <BrandedShowcaseLayout />
    </BrandProvider>
  );
};
